'use client'

import { useCallback, useEffect, useState } from 'react'

/**
 * Form state that survives a refresh, a closed tab and the back button. Read after mount, so the
 * server render and the first client render agree; `ready` is false until then.
 * Storage can throw (private mode, blocked site data) - the form then simply does not persist.
 */
export function useStoredState<T extends object>(key: string, initial: T) {
  const [value, setValue] = useState(initial)
  const [ready, setReady] = useState(false)

  useEffect(() => {
    try {
      const stored = window.localStorage.getItem(key)
      // merge over the initial shape, so a field added later still gets its default
      if (stored) setValue((current) => ({ ...current, ...JSON.parse(stored) }))
    } catch {}
    setReady(true)
  }, [key])

  useEffect(() => {
    if (!ready) return
    try {
      window.localStorage.setItem(key, JSON.stringify(value))
    } catch {}
  }, [key, ready, value])

  const clear = useCallback(() => {
    try {
      window.localStorage.removeItem(key)
    } catch {}
  }, [key])

  return [value, setValue, ready, clear] as const
}
